import { filter, forEach, isEmpty, map, omit } from 'lodash';
import {
  CREATE_PROJECT_ASSIGN_FLOW_SUCCESS,
  GET_PROJECTS_SUCCESS,
  GET_PROJECT_SUCCESS,
  ASSIGN_FLOW_TO_PROJECT_SUCCESS,
  ASSIGN_FLOW_TO_PROJECT_FAILURE,
  SET_SELECTED_PROJECT_FLOW,
  CLEAR_SELECTED_PROJECT_FLOW,
  UPDATE_PROJECT_METAINFO_SUCCESS,
  UPDATE_PROJECT_DOCUMENTATION_SUCCESS,
  GET_PROJECT_DOCUMENTATION_SUCCESS,
  GET_PIPELINES_SUCCESS,
  DELETE_PROJECTS_SUCCESS,
} from 'js/constants/actionTypes';

/**
 * Local State for Projects
 * @type Object
 */
const initialState = {
  byIds: {},
  docById: {},
  selectedFlow: { id: null, type: null },
  projectById: {},
};

const ProjectReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_PIPELINES_SUCCESS:
      return getPipelines(state, action);

    case CREATE_PROJECT_ASSIGN_FLOW_SUCCESS:
    case UPDATE_PROJECT_METAINFO_SUCCESS:
      return getProjectMeta(state, action);

    case GET_PROJECTS_SUCCESS:
      return setProjects(state, action);

    case GET_PROJECT_SUCCESS:
      return upsertSingleProject(state, action);

    case ASSIGN_FLOW_TO_PROJECT_SUCCESS:
      return assignFlowToProject(state, action);

    case ASSIGN_FLOW_TO_PROJECT_FAILURE:
      return { ...state, selectedFlow: { id: null, type: null } };

    case UPDATE_PROJECT_DOCUMENTATION_SUCCESS:
    case GET_PROJECT_DOCUMENTATION_SUCCESS:
      return updateProjectDoc(state, action);

    case SET_SELECTED_PROJECT_FLOW:
      return setSelectedFlow(state, action);

    case CLEAR_SELECTED_PROJECT_FLOW:
      return clearSelectedFlow(state, action);

    case DELETE_PROJECTS_SUCCESS:
      return deleteProject(state, action);

    default:
      return state;
  }
};

/**
 * @desc get all the pipeline datas, and extract project mapping
 */

function getPipelines(state, action) {
  const {
    result: { flows },
    entities: { flowNodes },
  } = action;
  const byIds = {};

  forEach(flows?.id, (flowNodeId) => {
    const { project_id } = flowNodes[flowNodeId];

    if (project_id) {
      if (!byIds[project_id]) {
        byIds[project_id] = [];
      }

      byIds[project_id].push(flowNodeId);
    }
  });

  return {
    ...state,
    byIds,
  };
}

/**
 * @desc received project meta information with the data_flows attached with project
 */
function getProjectMeta(state, action) {
  const projects = action?.entities?.projects;
  const data = (() => {
    if (projects) {
      return Object.keys(projects).reduce((acc, cur) => {
        acc[cur] = projects[cur].flows.map((flow) => flow.id);
        return acc;
      }, {});
    }
    return {};
  })();

  return {
    ...state,
    byIds: {
      ...state?.byIds,
      ...data,
    },
    selectedFlow: { id: null, type: null },
  };
}

/**
 * updating project doc and updating local store
 */
function updateProjectDoc(state, action) {
  const {
    result,
    requestInfo: { id },
  } = action;
  return {
    ...state,
    docById: {
      [id]: isEmpty(result) ? null : result[0],
    },
  };
}

/**
 * Pull all the projects and store into local state
 */
function setProjects(state, action) {
  let {
    entities: { projects },
    result,
  } = action;

  let byIds = {};
  if (result) {
    byIds = result.reduce((acc, cur) => {
      acc[cur] = map(projects[cur].data_flows, 'id');

      return acc;
    }, {});
  }

  let summaryById = {};
  if (projects) {
    summaryById = Object.values(projects).reduce((acc, v) => {
      acc[v.id] = { data_flows: v.flows_count };

      return acc;
    }, {});
  }

  return {
    ...state,
    listing: result,
    lastUpdated: action.receivedAt,
    byIds,
    error: null,
    summaryById,
  };
}

function upsertSingleProject(state, action) {
  const { entities: { projects } = {}, result } = action;
  if (!result || !projects) return state;

  const projectId = result;
  const flowsCount = projects[projectId]?.flows.length || 0;

  return {
    ...state,
    projectById: {
      ...(state.projectById || {}),
      [projectId]: { id: projectId, count: flowsCount },
    },
  };
}

/**
 * @desc Assign flow to project
 */
function assignFlowToProject(state, action) {
  const {
    result,
    requestInfo: { projectId },
  } = action;

  return {
    ...state,
    byIds: {
      ...state.byIds,
      [projectId]: map(result, 'id'),
    },
    selectedFlow: { id: null, type: null },
  };
}

/**
 * @desc Set the selected flow on which actions will happen
 */
function setSelectedFlow(state, action) {
  return {
    ...state,
    selectedFlow: {
      id: action?.data?.id || null,
      type: action?.data?.type || null,
    },
  };
}

function clearSelectedFlow(state, action) {
  return {
    ...state,
    selectedFlow: { id: null, type: null },
  };
}

function deleteProject(state, action) {
  const {
    requestInfo: { id: deletedId },
  } = action;
  const { byIds, listing } = state;
  const newSortedIds = omit(byIds, [deletedId]);
  const newListing = filter(listing, (item) => item !== deletedId);

  return {
    ...state,
    byIds: newSortedIds,
    listing: newListing,
  };
}

export default ProjectReducer;
